import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DocumentSchema, SchemaField, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { FilterGroup, Filter, FilterGroupType, RuleOperator } from 'src/app/modules/core/store/models/filters.model';
import { Workflow } from 'src/app/modules/core/store/models/workflow.model';
import { Store } from '@ngrx/store';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { first, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { FilterDisplayBuilder } from './filter-display-builder.service';
import { DurationService } from '@app/modules/core/services/duration/duration.service';
import { UserProfileService } from '@app/modules/core/services/user-profile.service';

@Component({
    selector: 'topo-filter-display',
    templateUrl: './filter-display.component.html',
    styleUrls: ['./filter-display.component.scss'],
    standalone: false
})
export class FilterDisplayComponent implements OnInit, OnDestroy {
  public destroy$ = new Subject<void>();
  public _filterGroup: FilterGroup;
  private _filterGroupValueBeforeEdit: FilterGroup;
  @Input()
  public set filterGroup(value: FilterGroup) {
    this._filterGroup = value;
    this.buildFilterDisplayText();
  }
  public get filterGroup(): FilterGroup {
    return this._filterGroup;
  }
  @Input() maxLength: number = 0;
  @Input() schema: DocumentSchema;
  @Input() workflow: Workflow;
  private _actionIfDescription: string;
  @Input()
  public set actionIfDescription(value: string) {
    this.translate
      .get(value)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(v => {
        this._actionIfDescription = v;
        this.buildFilterDisplayText();
      });
  }
  private _actionResultDescription: string;
  @Input()
  public set actionResultDescription(value: string) {
    this.translate
      .get(value)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(v => {
        this._actionResultDescription = v;
        this.buildFilterDisplayText();
      });
  }
  @Input() showInstructions = true;
  @Input() lineClamp: number;
  private _disabled: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
  }
  get disabled() {
    return this._disabled;
  }
  @Input() lookupTargetSchemaFields: SchemaField[];
  @Input() useForDatamart: boolean;

  @Output() filterChanged: EventEmitter<FilterGroup> = new EventEmitter<FilterGroup>();
  showFilterDialog: boolean;
  filterDisplayText: string;
  showNewFilterButton: boolean;
  unitsConversionValue: number;
  distanceName: string;
  andText: string;
  orText: string;
  dateFilterValues = [
    RuleOperator.DateIsWithinLast,
    RuleOperator.DateIsNotWithinLast,
    RuleOperator.DateIsWithinNext,
    RuleOperator.DateIsNotWithinNext,
    RuleOperator.DateIsGreaterThan,
    RuleOperator.DateIsLessThan
  ];

  constructor(
    private store: Store<fromUserProfile.State>,
    private translate: TranslateService,
    private durationService: DurationService,
    private userProfileService: UserProfileService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromUserProfile.selectDistanceUnit)
      .pipe(takeUntil(this.destroy$))
      .subscribe(unit => {
        if (unit?.toLocaleLowerCase() === 'miles') {
          this.unitsConversionValue = 1609;
          this.distanceName = this.translate.instant('UNITS.DISTANCE.MILES-ABBREVIATED-LOWER');
        } else {
          this.unitsConversionValue = 1000;
          this.distanceName = this.translate.instant('UNITS.DISTANCE.KILOMETERS-ABBREVIATED-LOWER');
        }
      });

    if (!!this._actionIfDescription) {
      this._actionIfDescription = `${this._actionIfDescription} `;
    }
    if (!!this._actionResultDescription) {
      this._actionResultDescription = ` ${this._actionResultDescription}`;
    }

    this.andText = this.translate.instant('FILTERS.AND');
    this.orText = this.translate.instant('FILTERS.OR');

    this.buildFilterDisplayText();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showFilterClick(): void {
    this._filterGroupValueBeforeEdit = this._filterGroup ? cloneDeep(this._filterGroup) : null;
    this.showFilterDialog = true;
  }

  newFilterClick(): void {
    this._filterGroupValueBeforeEdit = this._filterGroup ? cloneDeep(this._filterGroup) : null;
    this.filterGroup = {
      uId: null,
      filters: [],
      filterGroups: [],
      groupType: FilterGroupType.And
    };

    this.showFilterDialog = true;
  }

  filterCancel(): void {
    this._filterGroup = this._filterGroupValueBeforeEdit;
    this.showFilterDialog = false;
    this.buildFilterDisplayText();
  }

  onfilterChanged(): void {
    this.filterGroup = this.validateChangedFilter(this.filterGroup);
    this._filterGroupValueBeforeEdit = this._filterGroup;
    this.filterChanged.emit(this.filterGroup);
    this.showFilterDialog = false;
    this.buildFilterDisplayText();
  }

  validateChangedFilter(filterGroup: FilterGroup): FilterGroup {
    if (!filterGroup) {
      return null;
    }
    const hasFilters = filterGroup.filters?.length > 0;
    const hasGroups = filterGroup.filterGroups?.length > 0;
    if (!hasFilters && !hasGroups) {
      return null;
    }

    const validatedGroup = {
      ...filterGroup
    } as FilterGroup;
    validatedGroup.filterGroups = filterGroup.filterGroups?.map(f => this.validateChangedFilter(f)).filter(g => g);
    validatedGroup.filters = filterGroup.filters
      ?.map(f => {
        if (f.operator > 0 && f.field) {
          return f;
        }
        return null;
      })
      .filter(f => f);
    return validatedGroup;
  }

  buildFilterDisplayText(): void {
    if (!this.filterGroup || (!this.filterGroup?.filters && !this.filterGroup?.filterGroups)) {
      this.showNewFilterButton = true;
      return;
    }

    if (this.filterGroup?.filters.length === 0 && this.filterGroup.filterGroups.length === 0) {
      this.showNewFilterButton = true;
      return;
    }

    this.showNewFilterButton = false;

    const builder = new FilterDisplayBuilder();
    this.processFilterGroup(this.filterGroup, builder);

    this.filterDisplayText = `${this._actionIfDescription}${builder.buildFilterDisplayHtml(this.maxLength)}${
      this._actionResultDescription
    }`;
  }

  processFilterGroup(filterGroup: FilterGroup, builder: FilterDisplayBuilder): void {
    for (let filter of filterGroup?.filters) {
      let fieldName = filter.field;
      let schemaField = null;
      if (!!this.schema) {
        schemaField = this.schema?.fields.find(f => f.systemName === filter.field);
        fieldName = schemaField?.displayName;
      }
      const operator = this.translate.instant(`FILTERS.OPERATOR-${filter.operator}`);

      if (!builder.isEmpty) {
        if (filterGroup.groupType === FilterGroupType.And) {
          builder.appendNormalText(this.andText);
        } else {
          builder.appendNormalText(this.orText);
        }
      }

      if (filter.operator === RuleOperator.IsEmpty || filter.operator === RuleOperator.IsNotEmpty) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        continue;
      }

      if (filter.operator === RuleOperator.In || filter.operator === RuleOperator.NotIn) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        if (schemaField?.dataType === FieldDataType.Enumeration) {
          const ids = filter?.value?.toString()?.split(',') ?? [];
          schemaField?.enumerationValues?.filter(v => ids.includes(v.value.toString())).forEach(v => builder.appendTag(v.name));
        } else if (schemaField?.dataType === FieldDataType.BusinessUnits) {
          const ids = filter?.value?.toString()?.split(',') ?? [];
          const units = this.userProfileService.getUserBusinessUnits();
          units?.filter(v => ids.includes(v.uId.toString())).forEach(v => builder.appendTag(v.name));
        } else {
          filter.value
            ?.split('||')
            .filter(v => v?.length > 0)
            .map(v => builder.appendTag(v));
        }
        continue;
      }

      if (filter.operator === RuleOperator.InASingleGeofence || filter.operator === RuleOperator.NotInASingleGeofence) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        continue;
      }

      if (filter.operator === RuleOperator.InProximity || filter.operator === RuleOperator.NotInProximity) {
        builder.appendBoldText(fieldName);
        builder.appendNormalText(operator);
        builder.appendBoldText(JSON.parse(filter.value)?.display);
        continue;
      }

      builder.appendBoldText(fieldName);
      builder.appendNormalText(operator);

      if (schemaField?.dataType === FieldDataType.Picklist) {
        const picklistValue = schemaField.picklistValues.find(pl => pl.uId === filter.value);
        builder.appendBoldText(!!picklistValue ? picklistValue.value : '-');
      }

      if (schemaField?.dataType === FieldDataType.Enumeration) {
        const enumerationValue = schemaField.enumerationValues.find(pl => pl.uId === filter.value);
        builder.appendBoldText(!!enumerationValue ? enumerationValue.name : '-');
      } else if (schemaField?.dataType === FieldDataType.Duration) {
        builder.appendBoldText(this.durationService.getDurationDisplay(schemaField, filter.value));
      } else if (schemaField?.dataType === FieldDataType.Distance) {
        let displayValue = 0;

        if (filter.value) {
          displayValue = filter.value / this.unitsConversionValue;
        }

        builder.appendBoldText(`${displayValue}${this.distanceName}`);
      } else if (schemaField?.dataType === FieldDataType.UId) {
        // assume workflow
        const workflowStep = this.workflow?.workflowSteps.find(ws => ws.uId === filter.value);
        builder.appendColoredText(workflowStep?.statusName, workflowStep?.statusHtmlColorCode);
      } else {
        builder.appendBoldText(filter.value);

        if (!!filter.options && this.dateFilterValues.includes(filter.operator)) {
          builder.appendBoldText(this.translate.instant(`FILTERS.FILTER-OPTIONS-${filter.options}`));
        }
      }
    }

    for (let group of filterGroup.filterGroups) {
      if (!builder.isEmpty) {
        if (filterGroup.groupType === FilterGroupType.And) {
          builder.appendNormalText(this.andText);
        } else {
          builder.appendNormalText(this.orText);
        }
      }

      this.processFilterGroup(group, builder);
    }
  }
}
